import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { Campo, Caricamento, Errore, Etichetta, Scheda, Vuoto, useAvvisi, useCarico } from '../componenti/base';
import { formattaData, plurale } from '../formato';
import { useSessione } from '../main';

interface StatoIntegrazione {
  configurata: boolean;
  autorizzata: boolean;
  clientId: string | null;
  clientSecretImpostato: boolean;
  credenzialiDaAmbiente: boolean;
  redirectUri: string | null;
  scope: string;
  aziendaEsternaId: string | null;
  aziendaEsternaNome: string | null;
  scadenzaToken: string | null;
  ultimaSincronizzazione: string | null;
  dataInizioImport: string | null;
  urlNotifica: string | null;
  webhookId: string | null;
  webhookAttivoDal: string | null;
  ultimaNotifica: string | null;
  sincronizzazioneAutomatica: boolean;
  intervalloMinuti: number;
  ultimoLog: VoceLog | null;
  coda: StatoCoda;
  codaPausaMs: number;
}
interface StatoCoda {
  inAttesa: number;
  fatte: number;
  abbandonate: number;
  piuVecchiaInAttesa: string | null;
  ultimaLavorata: string | null;
  messeDaParte: {
    idEsterno: string;
    tipoEvento: string;
    ricevutaIl: string;
    ultimoErrore: string | null;
  }[];
}
interface VoceLog {
  id: string;
  iniziataIl: string;
  conclusaIl: string | null;
  esito: string;
  documentiCreati: number;
  documentiAggiornati: number;
  documentiInvariati: number;
  documentiAgganciati: number;
  messaggio: string | null;
}
interface Azienda {
  id: number;
  name: string;
}

export function Integrazioni() {
  const { organizzazione, utente } = useSessione();
  const avvisa = useAvvisi();
  const [parametri, setParametri] = useSearchParams();
  const stato = useCarico(() => api.get<StatoIntegrazione>('/api/integrazioni/fatture-in-cloud'), []);
  const log = useCarico(() => api.get<VoceLog[]>('/api/integrazioni/log'), []);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [sincronizzando, setSincronizzando] = useState(false);
  const [aziende, setAziende] = useState<Azienda[] | null>(null);

  // Esito del ritorno dal redirect OAuth.
  useEffect(() => {
    const esito = parametri.get('esito');
    if (!esito) return;
    if (esito === 'ok') avvisa('Integrazione autorizzata.');
    else if (esito === 'scegli-azienda') avvisa('Autorizzata. Ora scegli quale azienda sincronizzare.', 'attenzione');
    else avvisa(parametri.get('messaggio') ?? 'Autorizzazione non riuscita.', 'errore');
    setParametri({}, { replace: true });
    stato.ricarica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const soloAdmin = utente.ruolo !== 'ADMIN';

  const salvaCredenziali = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.put('/api/integrazioni/fatture-in-cloud/credenziali', { clientId, clientSecret });
      avvisa('Credenziali salvate, cifrate a database.');
      setClientId('');
      setClientSecret('');
      stato.ricarica();
    } catch (err) {
      avvisa(err instanceof Error ? err.message : 'Salvataggio non riuscito.', 'errore');
    }
  };

  const autorizza = async () => {
    try {
      const { url } = await api.post<{ url: string }>('/api/integrazioni/fatture-in-cloud/autorizza');
      location.href = url;
    } catch (err) {
      avvisa(err instanceof Error ? err.message : 'Non è stato possibile avviare l’autorizzazione.', 'errore');
    }
  };

  const sincronizza = async () => {
    setSincronizzando(true);
    try {
      const r = await api.post<{ creati: number; aggiornati: number; invariati: number; errori: unknown[] }>(
        '/api/integrazioni/fatture-in-cloud/sincronizza',
      );
      avvisa(
        `Sincronizzazione conclusa: ${r.creati} nuovi, ${r.aggiornati} aggiornati, ${r.invariati} invariati${
          r.errori.length ? `, ${plurale(r.errori.length, 'errore', 'errori')}` : ''
        }.`,
        r.errori.length ? 'attenzione' : 'ok',
      );
      stato.ricarica();
      log.ricarica();
    } catch (err) {
      avvisa(err instanceof Error ? err.message : 'Sincronizzazione non riuscita.', 'errore');
    } finally {
      setSincronizzando(false);
    }
  };

  if (stato.caricamento) return <Caricamento />;
  if (stato.errore || !stato.dati) return <div className="p-6"><Errore testo={stato.errore ?? 'Errore'} riprova={stato.ricarica} /></div>;
  const s = stato.dati;

  return (
    <div className="pagina space-y-5">
      <header>
        <Link to="/impostazioni" className="text-xs text-inchiostro-500 underline underline-offset-2">
          ← Impostazioni
        </Link>
        <h1 className="mt-1 text-lg font-semibold tracking-tight">Fatture in Cloud</h1>
        <p className="text-sm text-inchiostro-500">
          Tiene allineate le fatture emesse e ricevute e lo stato dei pagamenti. Mastro legge soltanto: non scrive
          niente nel gestionale.
        </p>
      </header>

      <Scheda titolo="Stato del collegamento">
        <dl className="space-y-2 text-sm">
          <Riga t="Credenziali OAuth" v={s.configurata ? <Etichetta testo="configurate" tono="ok" /> : <Etichetta testo="mancanti" tono="attenzione" />} />
          <Riga t="Autorizzazione" v={s.autorizzata ? <Etichetta testo="attiva" tono="ok" /> : <Etichetta testo="da fare" tono="attenzione" />} />
          {s.clientId && <Riga t="Client ID" v={<span className="font-mono text-xs">{s.clientId}</span>} />}
          {s.credenzialiDaAmbiente && (
            <Riga t="Origine credenziali" v={<span className="text-xs text-inchiostro-500">dal file .env</span>} />
          )}
          <Riga
            t="Azienda collegata"
            v={s.aziendaEsternaNome ?? s.aziendaEsternaId ?? <span className="text-inchiostro-400">nessuna</span>}
          />
          <Riga
            t="Ultima sincronizzazione"
            v={
              s.ultimaSincronizzazione
                ? `${formattaData(s.ultimaSincronizzazione.slice(0, 10), organizzazione.formatoData)} ${s.ultimaSincronizzazione.slice(11, 16)}`
                : 'mai'
            }
          />
          <Riga
            t="Scadenza del token"
            v={
              s.scadenzaToken
                ? `${formattaData(s.scadenzaToken.slice(0, 10), organizzazione.formatoData)} ${s.scadenzaToken.slice(11, 16)} — si rinnova da solo`
                : '—'
            }
          />
        </dl>

        <div className="mt-4 flex flex-wrap gap-2 border-t border-inchiostro-200 pt-4">
          <button className="bottone-secondario" onClick={autorizza} disabled={!s.configurata || soloAdmin}>
            {s.autorizzata ? 'Riautorizza' : 'Autorizza su Fatture in Cloud'}
          </button>
          <button className="bottone-primario" onClick={sincronizza} disabled={!s.autorizzata || !s.aziendaEsternaId || sincronizzando}>
            {sincronizzando ? 'Sincronizzazione…' : 'Sincronizza adesso'}
          </button>
          {s.autorizzata && (
            <button
              className="bottone-secondario"
              disabled={soloAdmin}
              onClick={async () => {
                try {
                  const a = await api.get<Azienda[]>('/api/integrazioni/fatture-in-cloud/aziende');
                  setAziende(a);
                } catch (e) {
                  avvisa(e instanceof Error ? e.message : 'Elenco non disponibile.', 'errore');
                }
              }}
            >
              Scegli azienda
            </button>
          )}
        </div>

        {aziende && (
          <ul className="mt-3 space-y-1 rounded-md border border-inchiostro-200 p-2">
            {aziende.map((a) => (
              <li key={a.id}>
                <button
                  className="w-full rounded px-2 py-1.5 text-left text-sm hover:bg-inchiostro-100"
                  onClick={async () => {
                    await api.put('/api/integrazioni/fatture-in-cloud/azienda', { id: String(a.id), nome: a.name });
                    avvisa(`Azienda collegata: ${a.name}`);
                    setAziende(null);
                    stato.ricarica();
                  }}
                >
                  {a.name} <span className="text-xs text-inchiostro-500">#{a.id}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Scheda>

      {!soloAdmin && (
        <Scheda
          titolo="Credenziali dell’app OAuth"
          sottotitolo="Si creano su fattureincloud.it/api-v2. Vengono salvate cifrate: da qui non si rileggono più."
        >
          <form onSubmit={salvaCredenziali} className="space-y-3">
            <Campo etichetta="Client ID">
              <input className="campo font-mono text-xs" required value={clientId} onChange={(e) => setClientId(e.target.value)} />
            </Campo>
            <Campo etichetta="Client Secret">
              <input
                className="campo font-mono text-xs"
                type="password"
                required
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
              />
            </Campo>
            <div className="rounded-md bg-inchiostro-50 p-3 text-xs text-inchiostro-600">
              <p>
                <strong>Redirect URI</strong> da registrare nell’app, identica:{' '}
                <code className="font-mono">{s.redirectUri ?? 'non impostata in .env'}</code>
              </p>
              <p className="mt-1">
                <strong>Scope</strong> richiesti, tutti in sola lettura: <code className="font-mono">{s.scope}</code>
              </p>
            </div>
            <div className="flex justify-end">
              <button type="submit" className="bottone-primario">
                Salva credenziali
              </button>
            </div>
          </form>
        </Scheda>
      )}

      <Scheda titolo="Da quando importare">
        <p className="text-sm text-attenuato">
          I gestionali tengono tutto lo storico, il previsionale ne usa poco. Lasciando vuoto entrano{' '}
          <strong>tutti</strong> i documenti presenti in Fatture in Cloud, anche di anni che il previsionale non guarda.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block text-xs text-attenuato">Importa i documenti dal</span>
            <input
              type="date"
              className="campo mt-1"
              max={new Date().toISOString().slice(0, 10)}
              defaultValue={s.dataInizioImport ?? ''}
              disabled={soloAdmin}
              onBlur={async (e) => {
                const valore = e.target.value || null;
                if (valore === (s.dataInizioImport ?? null)) return;
                try {
                  await api.put('/api/integrazioni/fatture-in-cloud/data-inizio', { data: valore });
                  stato.ricarica();
                  avvisa(
                    valore
                      ? `Da adesso si importa dal ${formattaData(valore, organizzazione.formatoData)}.`
                      : 'Da adesso si importa tutto lo storico.',
                    'ok',
                  );
                } catch (err) {
                  avvisa(err instanceof Error ? err.message : 'Modifica non riuscita.', 'errore');
                }
              }}
            />
          </label>
          <p className="text-xs text-attenuato max-w-md">
            La data conviene sceglierla pensando al <strong>ritardo di pagamento</strong>: è l’unica cosa che Mastro
            impara dallo storico, e ha bisogno di abbastanza fatture già chiuse per ogni controparte. Con clienti che
            fatturano di rado, un anno solo non basta.
          </p>
        </div>
      </Scheda>

      <Scheda titolo="Notifiche in tempo reale">
        <p className="text-sm text-attenuato">
          Con le notifiche accese, una fattura entra <strong>nel momento in cui il gestionale la riceve</strong>,
          non al giro successivo. La lettura all’indietro serve solo per il primo import.
        </p>

        {s.webhookId ? (
          <div className="mt-3 space-y-2 text-sm">
            <Riga t="Stato" v={<Etichetta testo="attive" tono="ok" />} />
            <Riga t="Sottoscrizione" v={<span className="font-mono text-xs">{s.webhookId}</span>} />
            <Riga
              t="Ultima notifica ricevuta"
              v={
                s.ultimaNotifica ? (
                  `${formattaData(s.ultimaNotifica.slice(0, 10), organizzazione.formatoData)} ${s.ultimaNotifica.slice(11, 16)}`
                ) : (
                  // Un webhook che tace non dà errori: semplicemente non succede
                  // niente. È il modo peggiore di rompersi, quindi si dice.
                  <span className="text-attenzione">nessuna ancora</span>
                )
              }
            />
            <button
              className="bottone-secondario mt-2"
              disabled={soloAdmin}
              onClick={async () => {
                try {
                  await api.del('/api/integrazioni/fatture-in-cloud/webhook');
                  stato.ricarica();
                  avvisa('Notifiche spente.', 'ok');
                } catch (err) {
                  avvisa(err instanceof Error ? err.message : 'Operazione non riuscita.', 'errore');
                }
              }}
            >
              Spegni le notifiche
            </button>
          </div>
        ) : (
          <div className="mt-3">
            <button
              className="bottone-primario"
              disabled={soloAdmin || !s.autorizzata || !s.aziendaEsternaId}
              onClick={async () => {
                try {
                  const r = await api.post<{ verificata: boolean; riusata: boolean }>(
                    '/api/integrazioni/fatture-in-cloud/webhook',
                    {},
                  );
                  stato.ricarica();
                  avvisa(
                    r.verificata
                      ? 'Notifiche accese e verificate.'
                      : 'Sottoscrizione creata, ma il gestionale non ha ancora verificato l’indirizzo.',
                    r.verificata ? 'ok' : 'attenzione',
                  );
                } catch (err) {
                  avvisa(err instanceof Error ? err.message : 'Operazione non riuscita.', 'errore');
                }
              }}
            >
              Accendi le notifiche
            </button>
            {s.urlNotifica && (
              <p className="mt-2 text-xs text-attenuato">
                Fatture in Cloud chiamerà <span className="font-mono">{s.urlNotifica}</span> per verificarlo, e da lì in
                poi per ogni fattura.
              </p>
            )}
          </div>
        )}
      </Scheda>

      <Scheda titolo="Coda delle notifiche">
        <p className="text-sm text-attenuato">
          Le notifiche si ricevono subito e si lavorano una alla volta. Il ritmo tiene le chiamate al gestionale sotto
          il suo limite di 300 ogni 5 minuti: nel giorno della fatturazione mensile ne arrivano a decine al minuto, e
          rispondere subito senza accodare significherebbe consumarlo tutto.
        </p>

        <div className="mt-3 space-y-2 text-sm">
          <Riga
            t="Da lavorare"
            v={
              s.coda.inAttesa === 0 ? (
                <span className="text-attenuato">nessuna</span>
              ) : (
                <>
                  {s.coda.inAttesa}
                  {s.coda.piuVecchiaInAttesa && (
                    <span className="ml-2 text-xs text-attenuato">
                      la più vecchia dalle {s.coda.piuVecchiaInAttesa.slice(11, 16)}
                    </span>
                  )}
                </>
              )
            }
          />
          <Riga t="Lavorate" v={s.coda.fatte} />
          <Riga
            t="Ultima lavorata"
            v={
              s.coda.ultimaLavorata ? (
                `${formattaData(s.coda.ultimaLavorata.slice(0, 10), organizzazione.formatoData)} ${s.coda.ultimaLavorata.slice(11, 16)}`
              ) : (
                <span className="text-attenuato">—</span>
              )
            }
          />
        </div>

        {s.coda.abbandonate > 0 && (
          <div className="mt-4">
            {/* Ognuna è un documento che il gestionale ha e noi no: non è una
                riga di registro, è una cosa da guardare. */}
            <p className="text-sm text-attenzione">
              {plurale(s.coda.abbandonate, 'notifica non entrata', 'notifiche non entrate')} dopo tutti i tentativi.
            </p>
            <table className="dati mt-2">
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>Quando</th>
                  <th>Perché</th>
                </tr>
              </thead>
              <tbody>
                {s.coda.messeDaParte.map((v) => (
                  <tr key={`${v.idEsterno}-${v.ricevutaIl}`}>
                    <td className="font-mono text-xs">{v.idEsterno}</td>
                    <td>
                      {formattaData(v.ricevutaIl.slice(0, 10), organizzazione.formatoData)} {v.ricevutaIl.slice(11, 16)}
                    </td>
                    <td className="text-xs text-attenuato">{v.ultimoErrore ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button
              className="bottone-secondario mt-3"
              disabled={soloAdmin}
              onClick={async () => {
                try {
                  const r = await api.post<{ rimesse: number }>(
                    '/api/integrazioni/fatture-in-cloud/coda/riprova',
                    {},
                  );
                  stato.ricarica();
                  avvisa(`${plurale(r.rimesse, 'notifica rimessa', 'notifiche rimesse')} in coda.`, 'ok');
                } catch (err) {
                  avvisa(err instanceof Error ? err.message : 'Operazione non riuscita.', 'errore');
                }
              }}
            >
              Riprova quelle non entrate
            </button>
          </div>
        )}
      </Scheda>

      <Scheda titolo="Sincronizzazione automatica">
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-1"
            checked={s.sincronizzazioneAutomatica}
            disabled={soloAdmin || !s.autorizzata}
            onChange={async (e) => {
              try {
                await api.put('/api/integrazioni/fatture-in-cloud/automatica', { attiva: e.target.checked });
                stato.ricarica();
              } catch (err) {
                avvisa(err instanceof Error ? err.message : 'Modifica non riuscita.', 'errore');
              }
            }}
          />
          <span>
            Sincronizza da sola
            {s.intervalloMinuti > 0 ? ` ogni ${s.intervalloMinuti} minuti` : ''}.
            {s.intervalloMinuti === 0 && (
              <span className="block text-xs text-attenzione">
                L’intervallo è a 0 nel file .env: il job non girerebbe comunque. Imposta SYNC_INTERVALLO_MINUTI e riavvia.
              </span>
            )}
          </span>
        </label>
      </Scheda>

      <Scheda titolo="Storico delle sincronizzazioni" senzaPadding>
        {(log.dati ?? []).length === 0 ? (
          <Vuoto titolo="Nessuna sincronizzazione eseguita" />
        ) : (
          <table className="dati">
            <thead>
              <tr>
                <th>Quando</th>
                <th>Esito</th>
                <th className="text-right">Nuovi</th>
                <th className="text-right">Agganciati</th>
                <th className="text-right">Aggiornati</th>
                <th className="text-right">Invariati</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {log.dati!.map((v) => (
                <tr key={v.id}>
                  <td className="num whitespace-nowrap">
                    {formattaData(v.iniziataIl.slice(0, 10), organizzazione.formatoData)}{' '}
                    <span className="text-inchiostro-400">{v.iniziataIl.slice(11, 16)}</span>
                  </td>
                  <td>
                    <Etichetta
                      testo={v.esito.toLowerCase().replace('_', ' ')}
                      tono={v.esito === 'OK' ? 'ok' : v.esito === 'PARZIALE' ? 'attenzione' : v.esito === 'IN_CORSO' ? 'info' : 'allerta'}
                    />
                  </td>
                  <td className="num text-right">{v.documentiCreati}</td>
                  <td className="num text-right">{v.documentiAgganciati}</td>
                  <td className="num text-right">{v.documentiAggiornati}</td>
                  <td className="num text-right">{v.documentiInvariati}</td>
                  <td className="max-w-64 truncate text-xs text-inchiostro-500" title={v.messaggio ?? ''}>
                    {v.messaggio ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Scheda>

      <p className="text-xs text-inchiostro-500">
        La sincronizzazione <strong>non crea movimenti bancari</strong> per le fatture che Fatture in Cloud dà per
        pagate: un movimento inventato falserebbe il saldo di cassa. Lo stato riferito dal gestionale viene mostrato
        accanto a quello calcolato dalla riconciliazione, non al posto suo.
      </p>
    </div>
  );
}

function Riga({ t, v }: { t: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-inchiostro-600">{t}</dt>
      <dd className="text-right font-medium">{v}</dd>
    </div>
  );
}
